import { useState } from "react";
import { Layers } from "lucide-react";
import { ExtendedCatalog } from "../../../store/Catalogs/types";
import { Skeleton } from "../../../components/ui/skeleton";
import { cn } from "@/dentlab/src/lib/utils";
import { useCentralStore } from "../../../store/Central";
import { useCatalogsStore } from "../../../store/Catalogs";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { CatalogRow } from "./catalog-row.component";

interface CatalogsListProps {
	catalogs: ExtendedCatalog[];
	selectedCatalogId: string | null;
	isLoading: boolean;
	onSelectCatalog: (catalogId: string | null) => void;
}

export const CatalogsList: React.FC<CatalogsListProps> = ({
	catalogs,
	selectedCatalogId,
	isLoading,
	onSelectCatalog,
}) => {
	const { organization, setDefaultCatalog } = useCentralStore((state) => ({
		organization: state.organization,
		setDefaultCatalog: state.setDefaultCatalog,
	}));
	const setCatalogDeactivated = useCatalogsStore(
		(state) => state.setCatalogDeactivated
	);

	const [catalogToDeactivate, setCatalogToDeactivate] =
		useState<ExtendedCatalog | null>(null);

	const defaultCatalogId =
		(organization as any)?.default_catalogs_default_id ||
		(organization as any)?.default_catalogs_custom_id;

	const handleSetDefault = async (
		catalog: ExtendedCatalog,
		e: React.MouseEvent
	) => {
		e.stopPropagation();

		if (catalog.is_deactivated || !catalog.id) {
			return;
		}

		if (catalog.id === defaultCatalogId) {
			await setDefaultCatalog(null, "default");
		} else {
			await setDefaultCatalog(
				catalog.id,
				catalog.catalog_type as "default" | "custom"
			);
		}
	};

	const handleToggleActivation = (
		catalog: ExtendedCatalog,
		e: React.MouseEvent
	) => {
		e.stopPropagation();

		if (!catalog.id) {
			return;
		}

		if (catalog.is_deactivated) {
			// Reactivation is harmless — apply immediately.
			setCatalogDeactivated(catalog.id, false);
		} else {
			// Deactivation removes the whole catalog from code matching, so confirm.
			setCatalogToDeactivate(catalog);
		}
	};

	const handleConfirmDeactivate = async () => {
		if (!catalogToDeactivate?.id) {
			return;
		}
		await setCatalogDeactivated(catalogToDeactivate.id, true);
		setCatalogToDeactivate(null);
	};

	if (isLoading) {
		return (
			<div className="space-y-1.5 p-3">
				{[1, 2, 3, 4].map((i) => (
					<Skeleton key={i} className="h-11 w-full rounded-lg" />
				))}
			</div>
		);
	}

	if (catalogs.length === 0) {
		return (
			<div className="p-6 text-center text-sm text-gray-400">
				Keine Kataloge verfügbar
			</div>
		);
	}

	const customCatalogs = catalogs.filter((c) => c.catalog_type === "custom");
	const defaultCatalogs = catalogs.filter(
		(c) => c.catalog_type === "default"
	);

	const isAllSelected = selectedCatalogId === "all-catalogs";

	return (
		<div className="p-2">
			{/* All Catalogs Option */}
			<button
				type="button"
				onClick={() => onSelectCatalog("all-catalogs")}
				className={cn(
					"group relative mb-3 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
					isAllSelected
						? "bg-blue-50/80 ring-1 ring-inset ring-blue-100"
						: "hover:bg-gray-100/70"
				)}
			>
				{isAllSelected && (
					<span className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-blue-500" />
				)}
				<Layers
					className={cn(
						"h-4 w-4 flex-shrink-0",
						isAllSelected ? "text-blue-500" : "text-gray-400"
					)}
				/>
				<span
					className={cn(
						"text-sm font-semibold",
						isAllSelected ? "text-blue-700" : "text-gray-700"
					)}
				>
					Alle Kataloge
				</span>
			</button>

			{/* Custom Catalogs */}
			{customCatalogs.length > 0 && (
				<div className="mb-4">
					<div className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
						Eigene Kataloge
					</div>
					<div className="space-y-0.5">
						{customCatalogs.map((catalog) => (
							<CatalogRow
								key={catalog.id}
								catalog={catalog}
								isSelected={selectedCatalogId === catalog.id}
								isDefault={catalog.id === defaultCatalogId}
								onSelect={onSelectCatalog}
								onSetDefault={handleSetDefault}
								onToggleActivation={handleToggleActivation}
							/>
						))}
					</div>
				</div>
			)}

			{/* Default Catalogs */}
			{defaultCatalogs.length > 0 && (
				<div>
					<div className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
						Standard Kataloge
					</div>
					<div className="space-y-0.5">
						{defaultCatalogs.map((catalog) => (
							<CatalogRow
								key={catalog.id}
								catalog={catalog}
								isSelected={selectedCatalogId === catalog.id}
								isDefault={catalog.id === defaultCatalogId}
								onSelect={onSelectCatalog}
								onSetDefault={handleSetDefault}
								onToggleActivation={handleToggleActivation}
							/>
						))}
					</div>
				</div>
			)}

			<AlertDialog
				open={!!catalogToDeactivate}
				onOpenChange={(open) => !open && setCatalogToDeactivate(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Katalog deaktivieren?
						</AlertDialogTitle>
						<AlertDialogDescription>
							Leistungen aus dem Katalog{" "}
							<strong>{catalogToDeactivate?.name}</strong> werden
							nicht mehr zur Auswahl angeboten. Sie können den
							Katalog jederzeit wieder aktivieren.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Abbrechen</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmDeactivate}>
							Deaktivieren
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};
